import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Clock, Stethoscope, Bed } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/ui'

interface TreatmentInfoSectionProps {
  duration?: string
  anesthesia?: string
  hospitalization?: string
  recovery?: string
  results?: string
  className?: string
}

const infoItems = [
  { key: 'duration', icon: Clock },
  { key: 'anesthesia', icon: Stethoscope },
  { key: 'hospitalization', icon: Bed },
] as const

export function TreatmentInfoSection({
  duration,
  anesthesia,
  hospitalization,
  className,
}: TreatmentInfoSectionProps) {
  const { t } = useTranslation('common')

  const values: Record<string, string | undefined> = {
    duration,
    anesthesia,
    hospitalization,
  }

  const visible = infoItems.filter((item) => values[item.key])

  if (visible.length === 0) return null

  return (
    <section className={cn('bg-ink-deep py-14 md:py-20', className)}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          title={t('common:treatment.infoTitle')}
          centered
          light
          className="mb-8 md:mb-10"
        />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5 }}
          className="border-y border-primary/20"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-primary/10">
            {visible.map(({ key, icon: Icon }) => (
              <div
                key={key}
                className="flex items-center gap-4 md:gap-5 py-5 md:py-7 px-4 md:px-6"
              >
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/30 text-primary">
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <span className="block text-[11px] uppercase tracking-[0.16em] text-white/50">
                    {t(`common:treatment.${key}`)}
                  </span>
                  <span className="block mt-0.5 font-display text-xl md:text-2xl italic text-white">
                    {values[key]}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
